/** Lightweight client-side error reporter.
 *  Captures `error` + `unhandledrejection` + manual `report()` and POSTs to backend.
 *  Does NOT bring any third-party dependency.
 */
import { api } from "$lib/api/client";

interface ClientError {
  message: string;
  source?: string;
  url: string;
  user_agent: string;
  stack?: string;
  level: "error" | "warn";
  context?: Record<string, unknown>;
}

const MAX_QUEUE = 20;
const queue: ClientError[] = [];
let installed = false;
let lastSentAt = 0;

async function flush() {
  if (queue.length === 0) return;
  const now = Date.now();
  if (now - lastSentAt < 800) return;
  lastSentAt = now;
  const batch = queue.splice(0, queue.length);
  try {
    await api("/client-errors", { method: "POST", body: JSON.stringify({ items: batch }) });
  } catch {
    // silent fail; do not requeue to avoid loops
  }
}

function enqueue(err: ClientError) {
  if (queue.length >= MAX_QUEUE) queue.shift();
  queue.push(err);
  setTimeout(flush, 800);
}

export function report(message: string, context?: Record<string, unknown>) {
  enqueue({
    message, level: "error", context,
    url: location.href, user_agent: navigator.userAgent,
  });
}

export function install() {
  if (installed) return;
  installed = true;
  window.addEventListener("error", (e) => {
    enqueue({
      message: e.message || "window error",
      source: e.filename, stack: e.error?.stack,
      url: location.href, user_agent: navigator.userAgent, level: "error",
    });
  });
  window.addEventListener("unhandledrejection", (e: any) => {
    const reason = e.reason;
    enqueue({
      message: typeof reason === "string" ? reason : reason?.message || "unhandled rejection",
      stack: reason?.stack,
      url: location.href, user_agent: navigator.userAgent, level: "error",
    });
  });
}
